import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

// CSS styling
import "../../styles/activitiesPublic.css";
import "../../styles/clientBook.css";

// moment js
import moment from "moment";

// HTML react parser
import parse, { attributesToProps, domToReact } from "html-react-parser";

// lucide-react
import { Eye } from "lucide-react";

// React lottie
import Lottie from "react-lotties";
import PaperPlane from "../../lotties/paperplane.json";

// image dummy
import NoWorkshopAvailable from "../../images/icons/NoWorkshopAvailable.png";
import EN_flag_icon from "../../images/icons/countryFlags/EN_flag_icon.png";
import ID_flag_icon from "../../images/icons/countryFlags/ID_flag_icon.png";

// Helpers
import { SITE_URL, absoluteUrl } from "../../helpers/seo";

// Components
import Seo, { organizationRef } from "../../components/Seo.js";
import Header from "../../components/Header.js";
import Footer from "../../components/Footer.js";
import ContainerText from "../../components/Container.js";

// apis
import { fetchBooks } from "../../api/books.js";

// The page already has its own <h1>, so an <h1> written in the CMS content becomes an <h2> that looks the same
const parseContent = (html) =>
  parse(html, {
    replace: (node) => {
      if (node.type === "tag" && node.name === "h1") {
        return (
          <h2 {...attributesToProps(node.attribs)} className="h1">
            {domToReact(node.children)}
          </h2>
        );
      }
    },
  });

const Books = () => {
  const navigate = useNavigate();

  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(false);

  // React lotties
  const lottieOptions = {
    loop: true,
    autoplay: true,
    animationData: PaperPlane,
    rendererSettings: {
      preserveAspectRatio: "xMidYMid slice",
    },
  };

  const handleFetchBooks = async () => {
    setLoading(true);
    const result = await fetchBooks();
    if (result && result.data) {
      // let finalBooks = result.data;
      // finalBooks =
      //   finalBooks && Array.isArray(finalBooks) && finalBooks.length > 0
      //     ? finalBooks.sort((a, b) => parseFloat(a.id) - parseFloat(b.id))
      //     : [];
      setBooks(result.data);
    }
    setLoading(false);
  };

  useEffect(() => {
    handleFetchBooks();
  }, []);

  return (
    <div>
      <Seo
        title="ESG & Sustainability Books"
        description="Browse books published by the Institute for Sustainability and Agility (ISA) on ESG and sustainability, including practical guides for businesses."
        path="/books"
        breadcrumbs={[{ name: "Books", path: "/books" }]}
        jsonLd={{
          "@type": "CollectionPage",
          name: "ESG & Sustainability Books",
          description:
            "Books on ESG and sustainability published by the Institute for Sustainability and Agility (ISA).",
          url: absoluteUrl("/books"),
          isPartOf: { "@id": `${SITE_URL}/#website` },
          publisher: organizationRef,
        }}
      />
      <Header />
      <div className="activityPublicContainer">
        <ContainerText
          props={
            <div
              className="activityPublicDetailCardContainerDesc"
              style={{ margin: "25px" }}
            >
              <div style={{ marginTop: "5em", marginBottom: "4em" }}>
                <h1
                  style={{
                    color: "#FF9F1E",
                    fontWeight: "700",
                    margin: 0,
                  }}
                  className="header seoH1Sized"
                >
                  Books
                </h1>
              </div>
              {loading ? (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    textAlign: "center",
                  }}
                >
                  <Lottie options={lottieOptions} height={200} width={400} />
                  <h4
                    style={{
                      color: "#0D4C86",
                    }}
                  >
                    LOADING
                  </h4>
                  <p
                    style={{
                      color: "#0D4C86",
                    }}
                  >
                    Processing your request, please wait...
                  </p>
                </div>
              ) : books && Array.isArray(books) && books.length > 0 ? (
                books.map((book) => {
                  const {
                    id,
                    img_url,
                    title,
                    description,
                    published_on,
                    language,
                    number_of_pages,
                  } = book || {};

                  return (
                    <div
                      key={id}
                      className="eachClientBook"
                      onClick={() => navigate(`/books/${id}`)}
                    >
                      <div className="eachClientBookImageContainer">
                        <img src={img_url} alt={title} loading="lazy" />
                        <div className="eachClientBookImageContainerOverlay">
                          <div className="eachClientBookImageContainerOverlayContent">
                            <Eye />
                            <div>View Author</div>
                          </div>
                        </div>
                      </div>
                      <div className="eachClientBookTitleDescContainer">
                        <div className="eachClientBookTitleStyle title">
                          <Link
                            to={`/books/${id}`}
                            onClick={(e) => e.stopPropagation()}
                            style={{ color: "inherit", textDecoration: "none" }}
                          >
                            {title}
                          </Link>
                        </div>
                        <div className="eachClientBookPublishedDateStyle paragrafDesc">
                          {`Published on: ${
                            published_on
                              ? moment(published_on).format("DD/MM/YYYY")
                              : "-"
                          }`}
                        </div>
                        <div className="eachClientBookPublishedDateStyle paragrafDesc">
                          <div>Language:</div>
                          <div>{`${language} ${
                            language && language === "English" ? `(EN)` : `(ID)`
                          }`}</div>
                          <img
                            src={
                              language === "English"
                                ? EN_flag_icon
                                : ID_flag_icon
                            }
                            alt="country flag"
                            loading="lazy"
                          />
                        </div>
                        <div className="eachClientBookPublishedDateStyle paragrafDesc">
                          {`Pages: ${number_of_pages}`}
                        </div>
                        <div className="eachClientBookDescriptionStyle paragraf">
                          {parseContent(description)}
                        </div>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div
                  style={{
                    width: "100%",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    textAlign: "center",
                    paddingBottom: "3em",
                  }}
                >
                  <div style={{ marginBottom: "3em" }}>
                    <img
                      src={NoWorkshopAvailable}
                      alt="Data not found icon"
                      width={200}
                    />
                  </div>
                  <h4
                    style={{
                      color: "#000000",
                    }}
                  >
                    No book available at this moment. Stay tune!
                  </h4>
                </div>
              )}
            </div>
          }
        />
      </div>
      <Footer />
    </div>
  );
};

export default Books;
